/**
 * Placement of the floating device labels in the Rack Explorer's 3D view.
 *
 * Labels never sit on top of each other or on the devices they describe: the
 * anchors (each device's projected screen point) are split into a left and a
 * right half, and each half stacks its labels in a column just outside the
 * cluster, in the same top-to-bottom order as the devices, so leader lines do
 * not cross. Each label may have its own height (a label carries only the
 * facts that are recorded). When a column cannot fit full labels, every label
 * drops to its compact size. Pure: screen pixels in, rectangles out, pinned by
 * `tests/calloutLayout.test.ts`.
 */

export interface CalloutAnchor {
  id: string;
  x: number;
  y: number;
  /** This label's full height; defaults to `sizes.full`. */
  full?: number;
  /** This label's compact height; defaults to `sizes.compact`. */
  compact?: number;
}

export interface CalloutBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface CalloutSizes {
  width: number;
  /** Height of a full label (name, IP, MAC, where). */
  full: number;
  /** Height of a compact label (name and IP). */
  compact: number;
  /** Vertical space between stacked labels. */
  gap: number;
  /** Horizontal distance kept between a column and the nearest device. */
  margin: number;
}

export interface PlacedCallout {
  id: string;
  side: 'left' | 'right';
  x: number;
  y: number;
  height: number;
  /** Leader line: from the label's inner edge to the anchor. */
  leader: { x1: number; y1: number; x2: number; y2: number };
}

export interface CalloutLayout {
  compact: boolean;
  /** The tallest placed label. */
  height: number;
  placed: PlacedCallout[];
}

export function layoutCallouts(anchors: CalloutAnchor[], bounds: CalloutBounds, sizes: CalloutSizes): CalloutLayout {
  if (!anchors.length) return { compact: false, height: sizes.full, placed: [] };

  // Balanced halves by screen x; ties broken by y so the split is stable.
  const byX = [...anchors].sort((a, b) => a.x - b.x || a.y - b.y);
  const half = Math.ceil(byX.length / 2);
  const left = byX.slice(0, half);
  const right = byX.slice(half);

  const available = bounds.bottom - bounds.top;
  const fullOf = (a: CalloutAnchor) => a.full ?? sizes.full;
  const compactOf = (a: CalloutAnchor) => a.compact ?? sizes.compact;
  const columnHeight = (items: CalloutAnchor[], h: (a: CalloutAnchor) => number) =>
    items.reduce((sum, a) => sum + h(a), 0) + Math.max(0, items.length - 1) * sizes.gap;
  const compact = Math.max(columnHeight(left, fullOf), columnHeight(right, fullOf)) > available;
  const heightOf = compact ? compactOf : fullOf;

  const minX = Math.min(...anchors.map((a) => a.x));
  const maxX = Math.max(...anchors.map((a) => a.x));
  const leftX = clamp(minX - sizes.margin - sizes.width, bounds.left, bounds.right - sizes.width);
  const rightX = clamp(maxX + sizes.margin, bounds.left, bounds.right - sizes.width);

  const column = (items: CalloutAnchor[], side: 'left' | 'right', x: number): PlacedCallout[] => {
    const sorted = [...items].sort((a, b) => a.y - b.y);
    const heights = sorted.map(heightOf);
    const tops = stack(sorted.map((a, i) => a.y - heights[i] / 2), heights, sizes.gap, bounds.top, bounds.bottom);
    return sorted.map((a, i) => {
      const edge = side === 'left' ? x + sizes.width : x;
      const h = heights[i];
      return { id: a.id, side, x, y: tops[i], height: h, leader: { x1: edge, y1: tops[i] + h / 2, x2: a.x, y2: a.y } };
    });
  };

  const placed = [...column(left, 'left', leftX), ...column(right, 'right', rightX)];
  return { compact, height: Math.max(0, ...placed.map((p) => p.height)), placed };
}

/**
 * Nudge sorted desired tops apart so each label clears the one above it by
 * `gap`, staying within [top, bottom] where the column allows it.
 */
function stack(desired: number[], heights: number[], gap: number, top: number, bottom: number): number[] {
  const n = desired.length;
  const tops = desired.map((d, i) => clamp(d, top, bottom - heights[i]));
  for (let i = 1; i < n; i++) tops[i] = Math.max(tops[i], tops[i - 1] + heights[i - 1] + gap);
  if (n && tops[n - 1] + heights[n - 1] > bottom) {
    tops[n - 1] = bottom - heights[n - 1];
    for (let i = n - 2; i >= 0; i--) tops[i] = Math.min(tops[i], tops[i + 1] - heights[i] - gap);
  }
  // A column taller than the space starts at the top rather than above it.
  if (n && tops[0] < top) {
    tops[0] = top;
    for (let i = 1; i < n; i++) tops[i] = Math.max(tops[i], tops[i - 1] + heights[i - 1] + gap);
  }
  return tops;
}

function clamp(v: number, lo: number, hi: number) {
  return Math.min(Math.max(v, lo), Math.max(lo, hi));
}
